import { PageDataHandler } from "@/lib/utility/data/page-data-handler";
import { AbstractPageDataProvider } from "@/lib/utility/data/page-data-provider.abstract";
import { IPageViewModel } from "@/lib/utility/data/page-view-model.interface";
import { DocumentDirectionEnum, IInvoice, Invoice } from "@/models/invoice.entity";
import invoiceFleetService from "@/services/mrfiktiv/services/fleet/invoiceFleetService";
import {
  MrfiktivFleetInvoiceControllerGetAllParamsGen,
  MrfiktivInvoiceViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { PaginatedBaseStore } from "@/store/paginated-base.store";
import store from "@/store/VuexPlugin";
import { Action, Module, Mutation, getModule } from "vuex-module-decorators";
import { FleetInvoiceDataAccessLayer } from "../access-layers/fleet-invoice.access-layer";
import { FleetManagerModule } from "./manager-fleet.store";
import { PageFilterTypes } from "@/lib/utility/data/page-filter-types.enum";
import { PaginationFilterListElement } from "../base-pagination.store";
import { InvoiceStatusEnum } from "@/lib/enum/invoice-status.enum";
import { IVSelectItem } from "@/lib/interfaces/v-select-item.interface";

@Module({
  dynamic: true,
  namespaced: true,
  name: "invoiceFleetStore",
  store
})
export class InvoiceFleetStore extends PaginatedBaseStore<IInvoice, MrfiktivFleetInvoiceControllerGetAllParamsGen> {
  _data = FleetInvoiceDataAccessLayer;
  _pageProvider = new (class extends AbstractPageDataProvider<IInvoice, MrfiktivFleetInvoiceControllerGetAllParamsGen> {
    async getPage(query: MrfiktivFleetInvoiceControllerGetAllParamsGen): Promise<IPageViewModel<IInvoice>> {
      const res = await invoiceFleetService.findAll(query, { ids: FleetManagerModule.selectedFleetIds ?? [] });

      const meta = res.meta;
      const data = (res.data?.map((d) => new Invoice(d)) ?? []) as IInvoice[];

      return { meta, data };
    }
  })();

  _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = [
    { key: "numberplate", type: PageFilterTypes.STRING, displayName: "objects.invoice.number" },
    {
      key: "status",
      type: PageFilterTypes.ENUM,
      config: {
        items: Object.values(InvoiceStatusEnum).map((status) => {
          return {
            text: `enums.InvoiceStatusEnum.${status}`,
            value: status
          } as IVSelectItem;
        }),
        itemValue: "value"
      },
      displayName: "objects.invoice.status"
    },
    {
      key: "direction",
      type: PageFilterTypes.ENUM,
      config: {
        items: Object.values(DocumentDirectionEnum).map((direction) => {
          return {
            text: `enums.DocumentDirectionEnum.${direction}`,
            value: direction
          } as IVSelectItem;
        }),
        itemValue: "value"
      },
      displayName: "objects.invoice.direction"
    },
    {
      key: "date",
      type: PageFilterTypes.DATE,
      displayName: "objects.invoice.date"
    }
  ].map((f) => new PaginationFilterListElement(f));

  _current: MrfiktivInvoiceViewModelGen | null = null;

  get current() {
    return this._current;
  }

  @Mutation
  _mutateCurrent(data: MrfiktivInvoiceViewModelGen) {
    this._current = data;
  }

  @Action
  setCurrent(data: MrfiktivInvoiceViewModelGen) {
    this.context.commit("_mutateCurrent", data);
  }

  @Action
  async getOne(data: { partnerId: string; fleetId: string; id: string }): Promise<MrfiktivInvoiceViewModelGen> {
    const response = await invoiceFleetService.findOne(data.partnerId, data.fleetId, data.id);

    this.context.commit("_mutateCurrent", response.data);

    return response.data;
  }
}

export const InvoiceFleetModule = getModule(InvoiceFleetStore);
